import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Database, Utensils, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CSVImport() {
  const navigate = useNavigate();
  const [foodBankFile, setFoodBankFile] = useState(null);
  const [recipesFile, setRecipesFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState(null); // 'food_bank' | 'recipes' | null
  const [results, setResults] = useState({ foodBank: null, recipes: null });
  const [generateImages, setGenerateImages] = useState(true);

  const handleFoodBankImport = async () => {
    if (!foodBankFile) return;
    
    setImporting(true);
    setStep('food_bank');
    
    try {
      const token = localStorage.getItem('coach_token');
      const formData = new FormData();
      formData.append('file', foodBankFile);
      
      const res = await fetch(`${API}/api/import/food-bank?delete_existing=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      setResults(prev => ({ ...prev, foodBank: data }));
      
    } catch (err) {
      setResults(prev => ({ ...prev, foodBank: { status: 'error', message: err.message } }));
    } finally {
      setImporting(false);
      setStep(null);
    }
  };

  const handleRecipesImport = async () => {
    if (!recipesFile) return;
    
    setImporting(true);
    setStep('recipes');
    
    try {
      const token = localStorage.getItem('coach_token');
      const formData = new FormData();
      formData.append('file', recipesFile);
      
      const res = await fetch(`${API}/api/import/recipes?generate_images=${generateImages}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      setResults(prev => ({ ...prev, recipes: data }));
      
    } catch (err) {
      setResults(prev => ({ ...prev, recipes: { status: 'error', message: err.message } }));
    } finally {
      setImporting(false);
      setStep(null);
    }
  };

  const renderResult = (result, type) => {
    if (!result) return null;
    
    const isSuccess = result.status === 'success';
    
    return (
      <div className={`mt-4 p-4 rounded-lg ${isSuccess ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          {isSuccess ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className={`font-medium ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
            {result.message}
          </span>
        </div>
        
        {isSuccess && (
          <div className="text-sm text-gray-600 space-y-1">
            {type === 'recipes' && result.updated > 0 ? (
              <>
                <div>✅ New: {result.imported}</div>
                <div>🔄 Updated: {result.updated}</div>
              </>
            ) : (
              <div>✅ Imported: {result.imported}</div>
            )}
            {type === 'recipes' && result.with_images > 0 && (
              <div>📸 With images: {result.with_images}</div>
            )}
            {result.errors > 0 && (
              <div className="text-orange-600">⚠️ Errors: {result.errors}</div>
            )}
            {result.warnings > 0 && (
              <div className="text-yellow-600">⚠️ Warnings: {result.warnings}</div>
            )}
          </div>
        )}
        
        {result.error_details && result.error_details.length > 0 && (
          <div className="mt-3">
            <div className="text-sm font-medium text-red-700 mb-1">Errors:</div>
            <div className="max-h-40 overflow-y-auto text-xs bg-white p-2 rounded border">
              {result.error_details.map((err, idx) => (
                <div key={idx} className="mb-1">
                  Row {err.row}: {err.error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-semibold text-gray-900">CSV Import</h1>
        </div>

        {/* Instructions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Import Instructions</CardTitle>
            <CardDescription>
              Import your data in the correct order for full integrity.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-50">Step 1</Badge>
              <span>Upload <strong>MASTER_FOOD_DB.csv</strong> (Food Bank)</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-50">Step 2</Badge>
              <span>Upload <strong>MEAL_BATCH.csv</strong> (Recipes)</span>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5" />
              <span className="text-blue-700">
                Recipe imports are <strong>additive</strong> - duplicates (same recipe_id) are updated, new recipes are added.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Food Bank */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Step 1: Import Food Bank</CardTitle>
            </div>
            <CardDescription>
              Upload MASTER_FOOD_DB.csv with ingredient definitions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFoodBankFile(e.target.files[0])}
                className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <Button
                onClick={handleFoodBankImport}
                disabled={!foodBankFile || importing}
              >
                {step === 'food_bank' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Import
              </Button>
            </div>
            {foodBankFile && (
              <div className="mt-2 text-sm text-gray-500">
                Selected: {foodBankFile.name}
              </div>
            )}
            {renderResult(results.foodBank, 'foodBank')}
          </CardContent>
        </Card>

        {/* Step 2: Recipes */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Utensils className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">Step 2: Import Recipes</CardTitle>
            </div>
            <CardDescription>
              Upload MEAL_BATCH.csv with recipe data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-3">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setRecipesFile(e.target.files[0])}
                className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
              />
              <Button
                onClick={handleRecipesImport}
                disabled={!recipesFile || importing || !results.foodBank?.status === 'success'}
                className="bg-green-600 hover:bg-green-700"
              >
                {step === 'recipes' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Import
              </Button>
            </div>
            
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={generateImages}
                onChange={(e) => setGenerateImages(e.target.checked)}
                className="rounded"
              />
              Generate images via Runware (slower but recommended)
            </label>
            
            {recipesFile && (
              <div className="mt-2 text-sm text-gray-500">
                Selected: {recipesFile.name}
              </div>
            )}
            {renderResult(results.recipes, 'recipes')}
          </CardContent>
        </Card>

        {/* Success Summary */}
        {results.foodBank?.status === 'success' && results.recipes?.status === 'success' && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">Import Complete!</h3>
              <p className="text-green-700">
                {results.foodBank.imported} food items and {results.recipes.total_processed || results.recipes.imported} recipes processed.
                {results.recipes.updated > 0 && ` (${results.recipes.updated} updated)`}
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate('/coach/recipe-library')}
              >
                View Recipe Library
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
